import React, { Component, createRef } from 'react'
import PropTypes from 'prop-types'

import { uniqueIdentificationGenerator } from '../../utils/uniqueIdentificationGenerator'

import warningIcon from 'images/warning-icon.svg'
import errorIcon from 'images/error-icon.svg'
import spinnerIcon from 'images/spinner-icon.svg'
import timesIcon from 'images/times-icon.svg'
import successIcon from 'images/success-icon.svg'

class AutoCompleteInput extends Component {
  static propTypes = {
    /* The input placeholder */
    placeholder: PropTypes.string,

    /* The text that will be displayed along with the input */
    label: PropTypes.string,

    /* The input name */
    name: PropTypes.string,

    /* The input id */
    id: PropTypes.string,

    /* The text that will be displayed below the input */
    helpText: PropTypes.string,

    /* The text that will be displayed below the input when the input is valid */
    successText: PropTypes.string,

    /* The text that will be displayed below the input when an error is found */
    errorText: PropTypes.string,

    /* The text that will be displayed below the input when an warning need to be displayed */
    warningText: PropTypes.string,

    /* The element classes */
    className: PropTypes.string,

    /* Initial value */
    value: PropTypes.string,

    /* Callback for when the input changes */
    onChange: PropTypes.number,

    /* Determine if the input is or not required */
    required: PropTypes.bool,

    /* Determine if the input is or not disabled */
    disabled: PropTypes.bool,

    /* Determine if the input is or not readonly */
    readOnly: PropTypes.bool,

    /* Function that will be triggered when the value changes. May return true or false to wheter the new value is or not valid */
    validateValue: PropTypes.func,

    /* Determine if the select options are shown or not */
    isOpen: PropTypes.bool,

    /* Determine if the select is multiple */
    isMulti: PropTypes.bool,

    /* A function that mandatorily receives a text and returns an array of options that will be loaded into the select */
    getAutocompleteOptions: PropTypes.func,
  }

  state = {
    /* The input value  */
    value: this.props.value || '',

    /* Stores the information of wheter the input is or not valid  */
    isValid: null,

    /* Determine if the select options are shown or not */
    isOpen: false,

    /* Determine if the select is multiple */
    isMulti: false,

    /* Determine if select options are being searched */
    isLoading: false,

    /* An array of options that will be loaded into the select */
    options: [],
  }

  ref = createRef()

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClick)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick)
  }

  handleClick = (e) => {
    if (!this.ref.current.contains(e.target)) {
      this.setState({
        isOpen: false,
      })
    }
  }

  getClasses() {
    const { disabled, readOnly } = this.props
    const { isValid } = this.state
    return `
      ${disabled ? 'disabled' : ''}
      ${isValid === false ? 'invalid' : ''}
      ${isValid === true ? 'valid' : ''}
      ${readOnly ? 'read-only' : ''}
    `
  }

  valueIsValid(value) {
    const { required, validateValue } = this.props

    if (validateValue) {
      return validateValue(value)
    }
    return required && value === ''
  }

  textHelperDisplay() {
    const { errorText, warningText, helpText, successText } = this.props
    const { isValid } = this.state

    if (isValid === true) {
      if (successText)
        return ['success', <img key='successIcon' src={successIcon} />, successText]
    } else if (isValid !== false) {
      if (helpText) return ['default', null, helpText]
    } else {
      if (errorText) {
        return ['error', <img key='errorIcon' src={errorIcon} />, errorText]
      }
      if (warningText) {
        return ['warning', <img key='warningIcon' src={warningIcon} />, warningText]
      }
    }
    return ['', null, null]
  }

  loadOptions = () => {
    const { value } = this.state
    const { getAutocompleteOptions } = this.props
    getAutocompleteOptions(value, (options) => {
      this.setState({
        options: options || [],
        isOpen: options.length > 0,
        isLoading: false,
      })
    })
  }

  validateValueAndChangeState(value, stateObject, callback = () => {}) {
    const isValid = this.valueIsValid(value)
    this.setState(
      {
        ...(stateObject || {}),
        isValid,
      },
      () => callback()
    )
  }

  isSelected = (opt) => {
    const { selectedOpt } = this.state

    return opt === selectedOpt
  }

  render() {
    const { label, name, required, disabled, className, id, placeholder } =
      this.props
    const { isOpen, value, options, isLoading, selectedOpt } = this.state

    const customId = id || uniqueIdentificationGenerator('autocomplete-input_')
    const customName =
      name || uniqueIdentificationGenerator('autocomplete-input_')
    const classes = this.getClasses()
    const [inputValidationClass, inputSmallIcon, inputSmallText] =
      this.textHelperDisplay()

    return (
      <>
        <div
          ref={this.ref}
          className={`input-group-container input-container autocomplete-container input-container-select ${
            disabled ? 'disabled' : ''
          } ${inputValidationClass} ${classes}`}
        >
          <label>
            {label}
            {required !== true && <i> - opcional</i>}
          </label>
          <div className={className || `form-control ${isOpen ? 'open' : ''}`}>
            <div className='autocomplete-group'>
              <input
                id={customId}
                name={customName}
                disabled={disabled}
                required={required}
                className={className || 'form-control autocomplete-input'}
                onChange={(e) =>
                  this.validateValueAndChangeState(
                    e.target.value,
                    { value: e.target.value, isLoading: true },
                    this.loadOptions
                  )
                }
                value={value}
                placeholder={placeholder}
                onBlur={() =>
                  !value &&
                  selectedOpt &&
                  this.validateValueAndChangeState(selectedOpt, {
                    value: selectedOpt,
                  })
                }
              />
              <span className='autocomplete-span'>
                {isLoading ? (
                  <img src={spinnerIcon} className='spinner-icon' />
                ) : (
                  !!value && (
                    <img
                      src={timesIcon}
                      className='clear'
                      onClick={() =>
                        this.validateValueAndChangeState('', { value: '' })
                      }
                    />
                  )
                )}
              </span>
            </div>
            {isOpen && (
              <ul className='input-container-select-dropdown no-margin-top'>
                {(options || []).map((textOption, i) => (
                  <li
                    key={i}
                    className={this.isSelected(textOption) ? 'active' : ''}
                    onClick={() =>
                      this.setState({
                        value: `${textOption}`,
                        selectedOpt: textOption,
                        isOpen: false,
                      })
                    }
                  >
                    {textOption}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {inputSmallText && (
            <small className={inputValidationClass}>
              {inputSmallIcon}
              {inputSmallText}
            </small>
          )}
        </div>
      </>
    )
  }
}

export default AutoCompleteInput
